import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmModal } from '@microservices/wiskey-react-components'
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridSortModel,
} from '@microservices/wiskey-react-table-component'
import { Grid } from '@mui/material'

import { ExportActionsPanel } from '@components/ConfigActionsPanel'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'

import { useEditConfig } from '@providers/EditConfigProvider'

import { generateConfigSelectColumn, generateDataGridActionsColumn } from '@helpers'
import { CONFIG_MENU_OPTION, ENTITY, FLEXIBLE } from '@constants'
import { ConfigRowsType } from '@types'

type EntityOrFormListProps<T> = {
  columns: GridColDef<ConfigRowsType>[]
  rows: T[]
  onDelete?: (id: string | number) => void
  onCopy?: (data: T) => void
  onEdit: (code: string, id: string | number) => void
  loading?: boolean
  onSetSorting?: (value: GridSortModel | null) => void
  onCustomDelete?: (id: string | number) => void
  currentSort?: GridSortModel
  type?: ENTITY
}

export const EntityOrFormList = <T extends { id: string | number; code?: string }>({
  columns,
  rows,
  onDelete,
  onCopy,
  onEdit,
  loading,
  onCustomDelete,
  type,
}: EntityOrFormListProps<T>) => {
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [currentRowId, setCurrentRowId] = useState<string | number>()

  const { t } = useTranslation()

  const { configMenuOption, selectedConfigId, handleSelectConfigId } = useEditConfig()

  const handleCopy = (params: GridCellParams<any, T>) => {
    onCopy?.(params.row)
  }

  const handleEdit = (params: GridCellParams<any, T>) => {
    onEdit?.(params.row.code, params.row.id)
  }

  const handleClickDelete = (params: GridCellParams<any, T>) => {
    if (params.row.id) {
      if (onCustomDelete) {
        onCustomDelete?.(params.row.id)

        return
      } else {
        setShowDeleteModal(true)
      }
      setCurrentRowId(params.row.id)
    }
  }

  const handleDelete = () => {
    if (currentRowId) {
      onDelete?.(currentRowId)
      setShowDeleteModal(false)
    }
  }

  const handleClose = () => setShowDeleteModal(false)

  const columnsWithActions = useMemo(
    () => [
      ...columns,
      configMenuOption &&
        configMenuOption !== CONFIG_MENU_OPTION.IMPORT &&
        generateConfigSelectColumn({
          selectedConfigId,
          onSelectRow: handleSelectConfigId,
        }),
      generateDataGridActionsColumn({
        onEdit: handleEdit,
        onCopy: handleCopy,
        onDelete: handleClickDelete,
      }),
    ],
    [columns, type, onCustomDelete, selectedConfigId, configMenuOption]
  )

  return (
    <Grid container spacing={2} sx={FLEXIBLE}>
      <EditConfigScriptDialog />
      {showDeleteModal && (
        <ConfirmModal
          actionBtnText={t('modal.delete.btn')}
          isShow={showDeleteModal}
          text={t('modal.delete.warning')}
          title={t('modal.delete.title')}
          containerStyle={{
            px: 0.5,
            py: 0.5,
            // borderRadius: 0
          }}
          onClose={handleClose}
          onConfirm={handleDelete}
        />
      )}
      <Grid container item sx={FLEXIBLE}>
        <DataGrid
          disableMultipleColumnsSorting
          hideFooter
          columns={columnsWithActions}
          loading={loading}
          rows={rows}
          sx={{ borderRadius: 0 }}
        />
      </Grid>
      <ExportActionsPanel />
    </Grid>
  )
}
